import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { acesso, type AccountActivationResult } from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { Alert } from '@/shared/ui/Alert';
import { Spinner } from '@/shared/ui/Spinner';

import { segredoDoLink } from './autocadastroLink';
import { PublicFrame } from './PublicFrame';
import { usePortalConfig } from './usePortalConfig';

type Estado =
  | { tipo: 'ativando' }
  | { tipo: 'ativada'; conta: AccountActivationResult }
  | { tipo: 'invalido' }
  | { tipo: 'falha'; mensagem: string };

/**
 * Ativacao da conta pelo link do e-mail (ADR 0096): `/ativar-conta/{id}#token=...`.
 *
 * O cadastro publico de corretores cria o acesso pendente de ativacao, e o link chega ao
 * e-mail informado. Abrir o link ativa a conta: a tela manda o segredo ao servidor uma
 * vez so, tira o segredo do endereco e mostra "Conta ativada" com o botao Entrar. O link
 * vencido, usado ou invalido tem uma resposta so, sem dizer qual dos tres.
 */
export function AccountActivationPage() {
  const { activationId = '' } = useParams();
  const location = useLocation();
  // O segredo e lido uma vez, antes de sair do endereco. Sem ele, o link ja e invalido.
  const [segredo] = useState(() => segredoDoLink(location.hash));
  const [estado, setEstado] = useState<Estado>(() =>
    segredo && activationId ? { tipo: 'ativando' } : { tipo: 'invalido' },
  );
  const enviado = useRef(false);

  useEffect(() => {
    // O link vale uma vez: a segunda chamada (o modo estrito do React em
    // desenvolvimento repete o efeito) seria recusada e trocaria a tela de sucesso
    // pela de link invalido.
    if (enviado.current) return;
    enviado.current = true;
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
    if (!segredo || !activationId) return;
    acesso.activateAccount(activationId, segredo).then(
      (conta) => setEstado({ tipo: 'ativada', conta }),
      (causa: unknown) => {
        if (causa instanceof ApiError && causa.code === ErrorCode.NotFound) {
          setEstado({ tipo: 'invalido' });
        } else if (causa instanceof ApiError && causa.code === ErrorCode.RateLimited) {
          setEstado({
            tipo: 'falha',
            mensagem:
              'Muitas tentativas em pouco tempo. Aguarde um instante e abra o link de novo.',
          });
        } else {
          setEstado({
            tipo: 'falha',
            mensagem:
              'Nao foi possivel ativar a conta agora. Tente abrir o link de novo em alguns minutos.',
          });
        }
      },
    );
  }, [activationId, segredo]);

  const slug = estado.tipo === 'ativada' ? estado.conta.tenant_slug : undefined;
  const { portal } = usePortalConfig(slug);

  return (
    <PublicFrame portal={portal}>
      <h1 className="text-xl font-semibold text-ink-900">Ativacao da conta</h1>
      <div className="mt-6 flex flex-col gap-4">
        {estado.tipo === 'ativando' ? (
          <div className="flex items-center gap-3 text-sm text-ink-600">
            <Spinner size={20} label="Ativando a conta" />
            Ativando a sua conta...
          </div>
        ) : null}
        {estado.tipo === 'ativada' ? (
          <>
            <Alert tone="success" title="Conta ativada">
              O seu acesso em {estado.conta.tenant_name} esta ativo. Entre com o e-mail{' '}
              {estado.conta.email} e a senha que voce escolheu no cadastro.
            </Alert>
            <div>
              <Link
                to={estado.conta.login_path}
                className="inline-flex h-10 items-center rounded-md bg-brand-500 px-5 text-sm font-medium text-white hover:bg-brand-600"
              >
                Entrar
              </Link>
            </div>
          </>
        ) : null}
        {estado.tipo === 'invalido' ? (
          <>
            <Alert tone="danger" title="Link de ativacao invalido">
              Este link de ativacao nao e valido ou venceu. Na tela de acesso, entre com
              o seu e-mail e senha para pedir um link novo.
            </Alert>
            <Link
              to="/entrar"
              className="text-sm font-medium text-brand-600 hover:underline"
            >
              Ir para a tela de acesso
            </Link>
          </>
        ) : null}
        {estado.tipo === 'falha' ? (
          <Alert tone="danger" title="Conta nao ativada">
            {estado.mensagem}
          </Alert>
        ) : null}
      </div>
    </PublicFrame>
  );
}
